import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import PageHeading from "../../baseComponents/PageHeading";
import NotificationMessages, { INotificationMessagesRef } from "../NotificationMessages";
import Section from "../../baseComponents/Section";
import SLEntriesGrid, { GridLoadingStatus } from "../SLEntriesGrid/SLEntriesGrid";
import SLSearchForm from "./SLSearchForm";
import { ISLEntryGridItem } from "../../schemas/SLEntryGridSchemas";
import { useLocation } from "react-router-dom";
import SLBatchPopupOverlay, { ISLBatchPopupOverlay } from "../SLBatchPopup/SLBatchPopup";
import SLActionsManager, { ISLActionsManager } from "../SLActionsManager/SLActionsManager";
import { ISearchState } from "./useSearchStateFromLocation";
import { SubledgerContext } from "../../contexts/SubledgerContext";
import { SLLoadingStatus, delay } from "../../utilities/utilities";
import { GridCommandToggle } from "../SLEntriesGrid/utilities";

enum SLSearchLoadingStatus {
  idle = "idle",
  pending = "pending",
  resolved = "resolved",
  rejected = "rejected"
}

const SLSearch: React.FunctionComponent<any> = () => {
  const { companyCodes, documentTypes } = useContext(SubledgerContext);
  const location = useLocation();
  const [loadingStatus, setLoadingStatus] = useState<SLSearchLoadingStatus>(SLSearchLoadingStatus.idle);
  const [gridItems, setGridItems] = useState<ISLEntryGridItem[]>([]);
  const [currentSearchState, setCurrentSearchState] = useState<ISearchState | undefined>(undefined);

  const slActionsManagerRef = useRef<ISLActionsManager["ref"]>(null);
  const slBatchPopupRef = useRef<ISLBatchPopupOverlay["ref"]>(null);
  const _notificationsRef = useRef<INotificationMessagesRef>(null);

  const processNewGridItems = (newItems: ISLEntryGridItem[], searchState: ISearchState) => {
    setCurrentSearchState(searchState);
    setGridItems(newItems);
  };

  const onNotification: INotificationMessagesRef["setMessage"] = async (type, messages, opts) => {
    if (_notificationsRef.current) {
      if (opts && opts.preventReset) {
        _notificationsRef.current.reset();
        await delay(1000);
      }
      _notificationsRef.current.setMessage(type, messages);
    }
  };

  useEffect(() => {
    if (companyCodes.status === SLLoadingStatus.Resolved && documentTypes.status === SLLoadingStatus.Resolved) {
      setLoadingStatus(SLSearchLoadingStatus.idle);
    }
  }, [companyCodes.status, documentTypes.status]);

  useEffect(() => {
    if (gridItems.length !== 0) {
      setLoadingStatus(SLSearchLoadingStatus.resolved);
    }
  }, [gridItems]);

  useEffect(() => {
    return () => {
      setLoadingStatus(SLSearchLoadingStatus.idle);
    };
  }, [location]);

  const setGridLoading = useCallback(
    (flag: boolean) => {
      if (flag) {
        setLoadingStatus(SLSearchLoadingStatus.pending);
      } else {
        setLoadingStatus(SLSearchLoadingStatus.resolved);
      }
    },
    [setLoadingStatus]
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onActionCallback = () => {};

  return (
    <React.Fragment>
      <SLBatchPopupOverlay ref={slBatchPopupRef} />
      <SLActionsManager
        ref={slActionsManagerRef}
        onNotifications={onNotification}
        batchPopup={slBatchPopupRef.current}
      />
      <div className="content">
        <PageHeading>Subledger Search</PageHeading>
        <NotificationMessages ref={_notificationsRef} />
        <Section label="Search Fields">
          <SLSearchForm
            setGridLoading={setGridLoading}
            setGridItems={processNewGridItems}
            searchState={currentSearchState}
          />
        </Section>
        <Section label="Search Results">
          <SLEntriesGrid
            items={gridItems}
            loading={GridLoadingStatus[loadingStatus]}
            batchPopup={slBatchPopupRef.current}
            actionsManager={slActionsManagerRef.current}
            notificationsFn={onNotification}
            onActionCallback={onActionCallback}
            postDisabled={GridCommandToggle.Removed}
            addPostersDisabled={GridCommandToggle.Enabled}
            exportEntriesDisabled={GridCommandToggle.Enabled}
            sendToPosterDisabled={GridCommandToggle.Enabled}
            approveEntriesDisabled={GridCommandToggle.Enabled}
            needsClarificationDisabled={GridCommandToggle.Enabled}
            recallEntriesDisabled={GridCommandToggle.Enabled}
            deleteDraftsDisabled={GridCommandToggle.Enabled}
            addAttachmentDisabled={GridCommandToggle.Enabled}
            deleteAttachmentsDisabled={GridCommandToggle.Enabled}
            useWeightageCalculator={true}
            routeState={() => ({ origin: "/slsearch", state: { searchState: currentSearchState } })}
          />
        </Section>
      </div>
    </React.Fragment>
  );
};

SLSearch.displayName = "SLSearch";

export default SLSearch;
